/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { getAvatar } from "Models/User";
import cn from "classnames";
import { Button } from "components/Button";
import {
  AutoCompletePlaces,
  FieldRow,
  FieldSet,
  FormSection,
  HR,
  Input,
  RSSelectField,
  SpanError,
  Stack,
} from "components/index";
import { getReservation } from "lib/tripsApi";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ROUTES from "routes";
import { useUser } from "state/UserContext";
import PageLayout from "templates/PageLayout";
import usePreviousRoute from "utils/usePreviousRoute";

import useReservationForm from "./useReservationForm";
import { getAttendees } from "lib/userApi";
import useFetcher from "hooks/useFetcher";
import SelectGuest from "../FlightForm/components/SelectGuest";
import { useTranslation } from "react-i18next";
import useEventRouting from "hooks/useEventRouting";

const ReservationForm = () => {
  const { t } = useTranslation();
  const { userData } = useUser();

  const { reservationId } = useParams();
  const isUpdating = reservationId !== "new";
  const { eventId } = useEventRouting();
  const { data: attendeesData } = useFetcher(() => getAttendees(eventId));
  const [reservation, setReservation] = useState({
    companions: [],
    start_date: "",
    end_date: "",
    stay_name: "",
    address: "",
    room_type: "",
    room_number: "",
    sleeps: "",
    rating: 1,
  });
  const goBack = usePreviousRoute(ROUTES.dashboard.base);
  const handleOnChange = ({ target: { name, value } }) => {
    setReservation((prev) => ({ ...prev, [name]: value }));
  };
  const removeError = (field) => {
    if (formErrors.hasOwnProperty(field)) {
      formErrors[field] = "";
    }
    setFormErrors(formErrors);
  };
  const getErrorMessage = (field) => {
    return formErrors.hasOwnProperty(field) ? formErrors[field] : "";
  };

  const prefill = async () => {
    const { data, ok } = await getReservation(reservationId, eventId);
    if (!ok) {
      return goBack();
    }
    const {
      start_date,
      end_date,
      stay_name,
      address,
      room_type_name: room_type,
      room_number,
      room_capacity: sleeps,
      guests,
    } = data;
    setReservation({
      start_date,
      end_date,
      stay_name,
      address,
      room_type,
      room_number,
      sleeps,
      companions: guests.reduce((acc, guest) => {
        if (guest.hasOwnProperty("hash_id")) return acc; // skip the user
        return { ...acc, [guest.id]: true };
      }, {}),
    });
  };
  useEffect(() => {
    if (isUpdating) prefill();
  }, []);
  const { onSubmit, isLoading, formErrors, setFormErrors } =
    useReservationForm(reservation);
  return (
    <PageLayout
      title={t("reservationForm.hotelReservation")}
      hasGoBack
      goBackMethod={() => goBack(ROUTES.dashboard.base)}
    >
      <Stack className="w-full -bg--theme-colors-surface-neutral-level02 border -border--theme-colors-border-neutral-11 -rounded-outer-container px-6 py-10 md:px-[54px] mt-5">
        <FormSection
          title={t("reservationForm.hotelReservationDetails")}
          note={t("reservationForm.passengersNote")}
        >
          {formErrors.passengers?.length > 0 && (
            <div className="relative">
              <SpanError error={formErrors.passengers} />
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-5">
            <SelectGuest
              className={cn("select-none pointer-events-none")}
              input={"main_user"}
              checked={true}
              isDisabled={true}
              inputName={"main_user"}
              first_name={userData.first_name}
              last_name={userData.last_name}
              imageSrc={getAvatar(userData)}
            />

            {attendeesData &&
              attendeesData?.companions_details?.length > 0 &&
              attendeesData?.companions_details?.map((companion, i) => (
                <SelectGuest
                  inputId={companion.id}
                  inputName={companion.id}
                  checked={!!reservation.companions[companion.id]}
                  first_name={companion.first_name}
                  last_name={companion.last_name}
                  imageSrc={getAvatar(companion)}
                  onChange={(e) => {
                    const { checked } = e.target;
                    setFormErrors((prev) => ({
                      ...prev,
                      passengers: "",
                    }));
                    if (checked === true) {
                      setReservation((prev) => ({
                        ...prev,
                        companions: {
                          ...prev.companions,
                          [companion.id]: true,
                        },
                      }));
                    } else {
                      setReservation((prev) => {
                        const companions = { ...prev.companions };
                        delete companions[companion.id];
                        return {
                          ...prev,
                          companions,
                        };
                      });
                    }
                  }}
                />
              ))}
          </div>
          <HR className="!my-0" />
          <FieldRow>
            <FieldSet isFullWidth>
              <Input
                name="start_date"
                inputClassName="min-h-[48px]"
                type="date"
                label={t("reservationForm.startDate")}
                required
                value={reservation.start_date}
                onChange={(e) => {
                  handleOnChange(e);
                  removeError("start_date");
                }}
                error={getErrorMessage("start_date").length > 0}
              />
              <SpanError error={getErrorMessage("start_date")}></SpanError>
            </FieldSet>
            <FieldSet isFullWidth>
              <Input
                name="end_date"
                inputClassName="min-h-[48px]"
                value={reservation.end_date}
                type="date"
                label={t("reservationForm.endDate")}
                required
                onChange={(e) => {
                  handleOnChange(e);
                  removeError("end_date");
                }}
                error={getErrorMessage("end_date").length > 0}
              />
              <SpanError error={getErrorMessage("end_date")}></SpanError>
            </FieldSet>
          </FieldRow>
          <HR className="!my-0" />
          <FieldSet isFullWidth>
            <Input
              name="stay_name"
              value={reservation.stay_name}
              label={t("reservationForm.stayName")}
              required
              placeholder={t("guestInfoPage.hotelPlaceholder")}
              onChange={(e) => {
                handleOnChange(e);
                removeError("stay_name");
              }}
              error={getErrorMessage("stay_name").length > 0}
            />
            <SpanError error={getErrorMessage("stay_name")}></SpanError>
          </FieldSet>
          {/* <FieldRow>
            <FieldSet>
              <Label className="text-white ps-2 font-bold ">{t("staySection.hotelStartsLabel")}</Label>
              <StarRating
                rating={reservation.rating}
                onRate={(rating) => {
                  handleOnChange({
                    target: {
                      name: "rating",
                      value: rating,
                    },
                  });
                  removeError("rating");
                }}
              />
            </FieldSet>
          </FieldRow> */}

          <FieldSet isFullWidth>
            <AutoCompletePlaces
              label={t("reservationForm.address")}
              required
              value={reservation.address}
              onSelect={(address) => {
                handleOnChange({
                  target: {
                    name: "address",
                    value: address,
                  },
                });
                removeError("address");
              }}
              onChange={(address) => {
                handleOnChange({
                  target: {
                    name: "address",
                    value: address,
                  },
                });
                removeError("address");
              }}
              error={getErrorMessage("address").length > 0}
            />
            <SpanError error={getErrorMessage("address")}></SpanError>
          </FieldSet>
          <HR className="!my-0" />
          <div className="grid sm:grid-cols-3 grid-cols-1 gap-5">
            <FieldSet isFullWidth>
              <Input
                label={t("reservationForm.roomType")}
                required
                name="room_type"
                value={reservation.room_type}
                placeholder={t("guestInfoPage.wingPlaceholder")}
                onChange={(e) => {
                  handleOnChange(e);
                  removeError("room_type");
                }}
                error={getErrorMessage("room_type").length > 0}
              />
              <SpanError error={getErrorMessage("room_type")}></SpanError>
            </FieldSet>
            <FieldSet isFullWidth>
              <Input
                name="room_number"
                value={reservation.room_number}
                label={t("reservationForm.roomNumber")}
                placeholder="001"
                required
                onChange={(e) => {
                  handleOnChange(e);
                  removeError("room_number");
                }}
                error={getErrorMessage("room_number").length > 0}
              />
              <SpanError error={getErrorMessage("room_number")}></SpanError>
            </FieldSet>
            <FieldSet isFullWidth>
              <RSSelectField
                id="sleeps"
                name="sleeps"
                label={t("reservationForm.sleepLabel")}
                required
                options={[
                  { label: "1", value: "1" },
                  { label: "2", value: "2" },
                  { label: "3", value: "3" },
                  { label: "4", value: "4" },
                  { label: "5", value: "5" },
                ]}
                value={
                  reservation.sleeps !== null &&
                  reservation.sleeps !== undefined &&
                  `${reservation.sleeps}`.length > 0
                    ? `${reservation.sleeps}`
                    : ""
                }
                onChange={(val) => {
                  handleOnChange({
                    target: {
                      name: "sleeps",
                      value: val,
                    },
                  });
                  removeError("sleeps");
                }}
                error={getErrorMessage("sleeps").length > 0}
              />
              <SpanError error={getErrorMessage("sleeps")}></SpanError>
            </FieldSet>
          </div>
          <Button
            variant="primary"
            onClick={onSubmit}
            isLoading={isLoading}
            fullWidth
          >
            {t("reservationForm.saveLabel")}
          </Button>
        </FormSection>
      </Stack>
    </PageLayout>
  );
};

export default ReservationForm;
